import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ArrowRight, Check, X, Package, Zap, Shield, Lock, Loader2, Gift, LogIn } from 'lucide-react';
import { Button } from '../components/ui/button';
import SimulationDashboard from '../components/SimulationDashboard';
import { useAuth } from '../context/AuthContext';
import CurrencySelector from '../components/CurrencySelector';
import { useCurrency } from '../context/CurrencyContext';

const PricingPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, token, isAuthenticated, refreshUser } = useAuth();
  const { format, currentCurrency } = useCurrency();
  const [selectedTier, setSelectedTier] = useState('growth');
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [selectedBundles, setSelectedBundles] = useState([]);
  const [showSimulation, setShowSimulation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const heroRingsRef = useRef(null);

  // Check for payment status on page load
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');
    const trial = searchParams.get('trial');

    if (trial === 'started') {
      setPaymentStatus({ type: 'success', message: 'Your 14-day free trial has started!' });
    } else if (status === 'success' && sessionId) {
      pollPaymentStatus(sessionId);
    } else if (status === 'cancelled') {
      setPaymentStatus({ type: 'info', message: 'Payment was cancelled. Feel free to try again.' });
    }
  }, [searchParams]);

  // Poll payment status
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPaymentStatus({ type: 'info', message: 'Payment status check timed out. Please check your email for confirmation.' });
      return;
    }

    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/checkout/status/${sessionId}`, { headers });
      if (!response.ok) throw new Error('Failed to check payment status');

      const data = await response.json();
      
      if (data.payment_status === 'paid') {
        setPaymentStatus({ type: 'success', message: 'Payment successful! Thank you for your purchase. Welcome to PriceStrike!' });
        if (refreshUser) await refreshUser();
        return;
      } else if (data.status === 'expired') {
        setPaymentStatus({ type: 'error', message: 'Payment session expired. Please try again.' });
        return;
      }

      setPaymentStatus({ type: 'pending', message: 'Payment is being processed...' });
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus({ type: 'error', message: 'Error checking payment status.' });
    }
  };

  // Mouse movement effect for hero rings
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!heroRingsRef.current) return;
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      const x = (clientX / innerWidth - 0.5) * 30;
      const y = (clientY / innerHeight - 0.5) * 30;
      heroRingsRef.current.style.transform = `translate(${x}px, ${y}px)`;
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const tiers = [
    {
      id: 'freemium',
      name: 'Freemium',
      price: 0,
      features: ['14-day free trial', '10 SKUs', 'Sweetspot', 'Price Hub', '1 User'],
      isFree: true,
      tagline: 'Try Before You Buy',
      description: 'Perfect for exploring PriceStrike capabilities risk-free.',
      benefits: [
        'Hands-on experience with core pricing tools',
        'Test Sweetspot for optimal price discovery',
        'Access Price Hub for market insights',
        'No credit card required'
      ],
      bestFor: 'Small businesses evaluating pricing solutions'
    },
    {
      id: 'starter',
      name: 'Starter',
      price: 99,
      features: ['500 SKUs', '1 User Seat', '10k API Calls', 'Email Support'],
      tagline: 'Start with Confidence',
      description: 'Essential tools for small retailers ready to price smarter.',
      benefits: [
        'See pricing risk across 500 products',
        'Clear recommendations you can understand',
        'Basic analytics and performance tracking',
        'Email support for getting started'
      ],
      bestFor: 'Small e-commerce stores & single-category retailers'
    },
    {
      id: 'growth',
      name: 'Growth',
      price: 299,
      popular: true,
      features: ['2.5k SKUs', '2 User Seats', '20k API Calls', 'Email Support'],
      tagline: 'Scale Your Pricing Operations',
      description: 'Advanced capabilities for growing multi-category businesses.',
      benefits: [
        'Handle 2,500 SKUs across multiple categories',
        'Collaborate with team members (2 seats)',
        'Higher API limits for integrations',
        'Advanced reporting and analytics'
      ],
      bestFor: 'Growing retailers & multi-category SMBs'
    },
    {
      id: 'scale',
      name: 'Scale',
      price: 799,
      features: ['10k SKUs', '3 User Seats', '30k API Calls', 'Priority Support'],
      tagline: 'Enterprise-Grade Performance',
      description: 'Full-featured solution for high-volume pricing operations.',
      benefits: [
        'Manage up to 10,000 SKUs at scale',
        'Priority 24/7 support for critical issues',
        'Team collaboration (3 seats)',
        'High-volume API access for automation'
      ],
      bestFor: 'Large retailers & high-volume operations'
    },
    {
      id: 'enterprise',
      name: 'Enterprise+',
      price: 'Custom',
      features: ['Unlimited SKUs', 'Users on request', 'Unlimited API Calls', 'Dedicated Manager'],
      tagline: 'Tailored for Your Business',
      description: 'Custom solutions for complex enterprise requirements.',
      benefits: [
        'Unlimited SKUs and API calls',
        'Dedicated success manager',
        'Custom integrations and connectors',
        'SLA-backed support with guaranteed uptime'
      ],
      bestFor: 'Enterprise retailers & integrators'
    }
  ];

  const featureModules = [
    {
      id: 'category-management',
      name: 'Category Management Suite',
      description: '6 features. Multi-category optimization',
      bundlePrice: 199,
      savings: 95,
      savingsPercent: 32,
      features: [
        { id: 'category-opt', name: 'Category Optimization', price: 49, included: true },
        { id: 'top-n', name: 'Top N Focus', price: 49 },
        { id: 'price-harm', name: 'Price Harmonization', price: 49 },
        { id: 'profit-max', name: 'Profit Maximization', price: 49 },
        { id: 'weighted-metrics', name: 'Weighted Metrics', price: 49 },
        { id: 'pricing-rules', name: 'Pricing Rules Engine', price: 49 }
      ]
    },
    {
      id: 'demand-forecasting',
      name: 'Demand Forecasting Suite',
      description: '4 features. Enterprise ML forecasting',
      bundlePrice: 149,
      savings: 47,
      savingsPercent: 24,
      features: [
        { id: 'time-series', name: 'Time-Series Forecasting', price: 49, included: true },
        { id: 'causal-model', name: 'Causal Modeling', price: 49 },
        { id: 'new-product-forecast', name: 'New Product Forecasting', price: 49 },
        { id: 'realtime-demand', name: 'Real-Time Demand Sensing', price: 49 }
      ]
    },
    {
      id: 'portfolio-optimization',
      name: 'Portfolio Optimization Suite',
      description: '4 features. Cross-product optimization',
      bundlePrice: 125,
      savings: 71,
      savingsPercent: 36,
      features: [
        { id: 'portfolio-rebal', name: 'Portfolio Rebalancing', price: 49 },
        { id: 'elasticity-map', name: 'Elasticity Mapping', price: 49 },
        { id: 'multi-cat-promo', name: 'Multi-Category Promotions', price: 49 },
        { id: 'risk-assess', name: 'Risk Assessment', price: 49 }
      ]
    },
    {
      id: 'strategic-planning',
      name: 'Strategic Planning Suite',
      description: '3 features. Long-term pricing strategy',
      bundlePrice: 99,
      savings: 48,
      savingsPercent: 33,
      features: [
        { id: 'strategic-pricing', name: 'Strategic Pricing', price: 49 },
        { id: 'competitive-resp', name: 'Competitive Response', price: 49 },
        { id: 'market-pos', name: 'Market Positioning', price: 49 }
      ]
    },
    {
      id: 'promotional-intelligence',
      name: 'Promotional Intelligence Suite',
      description: '2 features. Smart promotion management',
      bundlePrice: 75,
      savings: 23,
      savingsPercent: 23,
      features: [
        { id: 'promo-opt', name: 'Promotion Optimization', price: 49 },
        { id: 'promo-impact', name: 'Promotional Impact Analysis', price: 49 }
      ]
    },
    {
      id: 'new-product-launch',
      name: 'New Product Launch Suite',
      description: '2 features. Launch pricing strategy',
      bundlePrice: 75,
      savings: 23,
      savingsPercent: 23,
      features: [
        { id: 'launch-pricing', name: 'Launch Pricing Strategy', price: 49 },
        { id: 'new-prod-forecast', name: 'New Product Forecasting', price: 49 }
      ]
    },
    {
      id: 'financial-planning',
      name: 'Financial Planning Suite',
      description: '3 features. Revenue & profit modeling',
      bundlePrice: 99,
      savings: 48,
      savingsPercent: 33,
      features: [
        { id: 'revenue-model', name: 'Revenue Modeling', price: 49 },
        { id: 'profit-plan', name: 'Profit Planning', price: 49 },
        { id: 'pnl-view', name: 'P&L Statement View', price: 49 }
      ]
    },
    {
      id: 'sku-optimization',
      name: 'SKU Optimization Suite',
      description: '7 features. Essential pricing optimization toolkit',
      bundlePrice: 225,
      savings: 118,
      savingsPercent: 34,
      features: [
        { id: 'price-opt', name: 'Price Optimization', price: 49, included: true },
        { id: 'revenue-max', name: 'Revenue Maximization', price: 49 },
        { id: 'ab-testing', name: 'A/B Testing', price: 49 },
        { id: 'price-increase', name: 'Price Increase Impact', price: 49 },
        { id: 'discount-opt', name: 'Discount Optimization', price: 49 },
        { id: 'positioning', name: 'Positioning Strategy', price: 49 },
        { id: 'margin-protect', name: 'Margin Protection', price: 49 }
      ]
    }
  ];

  const toggleBundle = (bundleId) => {
    setSelectedBundles(prev => 
      prev.includes(bundleId) 
        ? prev.filter(id => id !== bundleId)
        : [...prev, bundleId]
    );
    const bundle = featureModules.find(m => m.id === bundleId);
    if (bundle && !selectedBundles.includes(bundleId)) {
      setSelectedFeatures(prev => prev.filter(fId => !bundle.features.some(f => f.id === fId)));
    }
  };

  const toggleFeature = (featureId, bundleId) => {
    if (selectedBundles.includes(bundleId)) return;
    
    setSelectedFeatures(prev => 
      prev.includes(featureId) 
        ? prev.filter(id => id !== featureId)
        : [...prev, featureId]
    );
  };

  const calculateTotal = () => {
    const tier = tiers.find(t => t.id === selectedTier);
    const tierPrice = typeof tier?.price === 'number' ? tier.price : 0;
    const bundleCost = selectedBundles.reduce((acc, bundleId) => {
      const bundle = featureModules.find(m => m.id === bundleId);
      return acc + (bundle?.bundlePrice || 0);
    }, 0);
    const featureCost = selectedFeatures.reduce((acc, featureId) => {
      for (const module of featureModules) {
        const feature = module.features.find(f => f.id === featureId);
        if (feature) return acc + feature.price;
      }
      return acc;
    }, 0);
    
    return tierPrice + bundleCost + featureCost;
  };

  const calculateSavings = () => {
    return selectedBundles.reduce((acc, bundleId) => {
      const bundle = featureModules.find(m => m.id === bundleId);
      return acc + (bundle?.savings || 0);
    }, 0);
  };

  const handleCheckout = async () => {
    // Redirect all signup/checkout to coming soon page
    navigate('/coming-soon');
    return;

    // For enterprise, redirect to contact
    if (selectedTier === 'enterprise') {
      navigate('/contact?plan=enterprise');
      return;
    }

    setIsLoading(true);
    setPaymentStatus(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/checkout/create-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          tier_id: selectedTier,
          bundle_ids: selectedBundles,
          feature_ids: selectedFeatures,
          origin_url: window.location.origin
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create checkout session');
      }

      const data = await response.json();
      
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      setPaymentStatus({ type: 'error', message: 'Failed to initiate checkout. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0F1C]">
      <Header />

      {/* Payment Status Banner */}
      {paymentStatus && (
        <div className={`fixed top-20 left-1/2 -translate-x-1/2 z-50 px-6 py-4 rounded-xl shadow-lg flex items-center gap-3 ${
          paymentStatus.type === 'success' ? 'bg-green-500/20 border border-green-500/50 text-green-400' :
          paymentStatus.type === 'error' ? 'bg-red-500/20 border border-red-500/50 text-red-400' :
          paymentStatus.type === 'pending' ? 'bg-yellow-500/20 border border-yellow-500/50 text-yellow-400' :
          'bg-blue-500/20 border border-blue-500/50 text-blue-400'
        }`}>
          {paymentStatus.type === 'pending' && <Loader2 className="w-5 h-5 animate-spin" />}
          {paymentStatus.type === 'success' && <Check className="w-5 h-5" />}
          <span>{paymentStatus.message}</span>
          <button onClick={() => setPaymentStatus(null)} className="ml-4 hover:opacity-70">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center justify-center bg-gradient-to-b from-[#0A0F1C] to-[#111827] overflow-hidden pt-40 pb-16 px-6">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none" ref={heroRingsRef}>
          <div className="w-[500px] h-[500px] rounded-full border border-[#2563EB]/15 animate-pulse" style={{ animationDuration: '4s' }} />
          <div className="absolute w-[400px] h-[400px] rounded-full border border-[#2563EB]/15 animate-pulse" style={{ animationDuration: '3.5s', animationDelay: '0.3s' }} />
          <div className="absolute w-[300px] h-[300px] rounded-full border border-[#2563EB]/15 animate-pulse" style={{ animationDuration: '3s', animationDelay: '0.6s' }} />
        </div>

        <div className="relative z-10 text-center max-w-4xl">
          <a 
            href="https://axiosera.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#2563EB]/10 border border-[#2563EB]/30 rounded-full px-6 py-2 mb-6 hover:bg-[#2563EB]/20 transition-colors"
          >
            <span className="text-[#2563EB] font-medium text-sm">An AxioSera Product</span>
          </a>
          <h1 className="text-5xl md:text-6xl font-bold text-[#F8FAFC] mb-6 leading-tight">
            Simple, Transparent
            <span className="bg-gradient-to-r from-[#2563EB] to-[#3B82F6] bg-clip-text text-transparent"> Pricing</span>
          </h1>
          <p className="text-xl text-[#94A3B8] mb-6 leading-relaxed max-w-2xl mx-auto">
            Start free, upgrade when ready. Pay only for what you need. No hidden fees, no surprises.
          </p>
          <div className="flex items-center justify-center gap-3">
            <span className="text-[#94A3B8] text-sm">Currency:</span>
            <CurrencySelector />
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-12 bg-[#0A0F1C] px-6">
        <div className="max-w-5xl mx-auto">
          {/* Try Free First Banner */}
          <div className="bg-gradient-to-r from-[#2563EB]/10 to-[#111827] border border-[#2563EB]/30 rounded-xl p-6 mb-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#2563EB]/20 rounded-full flex items-center justify-center">
                <Shield className="w-6 h-6 text-[#2563EB]" />
              </div>
              <div>
                <p className="text-[#F8FAFC] font-semibold">Not sure yet? Try our free Pricing Health Check</p>
                <p className="text-[#94A3B8] text-sm">Analyze up to 50 products free. No signup required.</p>
              </div>
            </div>
            <Link to="/pricing-check">
              <Button variant="outline" className="border-[#2563EB] text-[#2563EB] hover:bg-[#2563EB]/10 whitespace-nowrap">
                Run Free Check →
              </Button>
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { step: '1', title: 'Choose a tier', desc: 'based on your SKU needs' },
              { step: '2', title: 'Add feature modules', desc: 'or buy individual features' },
              { step: '3', title: 'Get instant access', desc: 'after payment' }
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-4 bg-[#111827]/50 border border-[#2563EB]/20 rounded-xl p-5">
                <div className="w-12 h-12 bg-[#2563EB] rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                  {item.step}
                </div>
                <div>
                  <p className="text-[#F8FAFC] font-semibold">{item.title}</p>
                  <p className="text-[#94A3B8] text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Step 1: Capacity Tiers */}
      <section className="py-16 bg-[#0A0F1C] px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#F8FAFC] mb-3">
              Step 1: Choose Your <span className="text-[#2563EB]">Capacity Tier</span>
            </h2>
            <p className="text-[#94A3B8] text-lg">Select the tier that matches your business size and needs</p>
          </div>

          <div className="grid md:grid-cols-5 gap-4">
            {tiers.map((tier) => (
              <div
                key={tier.id}
                onClick={() => setSelectedTier(tier.id)}
                className={`relative bg-gradient-to-br from-[#111827] to-[#0A0F1C] border rounded-2xl p-5 cursor-pointer transition-all duration-300 hover:scale-105 ${
                  selectedTier === tier.id
                    ? 'border-[#2563EB] shadow-xl shadow-[#2563EB]/20 ring-2 ring-[#2563EB]'
                    : 'border-[#2563EB]/20 hover:border-[#2563EB]/60'
                } ${tier.isFree ? 'bg-gradient-to-br from-green-900/20 to-[#0A0F1C]' : ''}`}
              >
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#2563EB] to-[#3B82F6] text-white px-3 py-1 rounded-full text-xs font-bold uppercase whitespace-nowrap">
                    Most Popular
                  </div>
                )}

                {tier.isFree && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-1 rounded-full text-xs font-bold uppercase flex items-center gap-1">
                    <Gift className="w-3 h-3" /> Free Trial
                  </div>
                )}

                {selectedTier === tier.id && (
                  <div className="absolute top-3 right-3 w-5 h-5 bg-[#2563EB] rounded-full flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}

                <h3 className="text-xl font-bold text-[#F8FAFC] mb-2 mt-2">{tier.name}</h3>
                
                <div className="flex items-baseline gap-1 mb-4">
                  {typeof tier.price === 'number' ? (
                    <>
                      <span className="text-xl font-semibold text-[#2563EB]">{currentCurrency.symbol}</span>
                      <span className="text-3xl font-bold text-[#F8FAFC]">{Math.round(tier.price * currentCurrency.rate)}</span>
                      <span className="text-[#94A3B8] text-sm">/mo</span>
                    </>
                  ) : (
                    <span className="text-2xl font-bold text-[#F8FAFC]">{tier.price}</span>
                  )}
                </div>

                <ul className="space-y-2">
                  {tier.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-xs">
                      <Check className="w-3 h-3 text-[#2563EB] flex-shrink-0" />
                      <span className="text-[#94A3B8]">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tier Summary Section */}
      <section className="py-16 bg-gradient-to-b from-[#111827] to-[#0A0F1C] px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#F8FAFC] mb-3">
              Plan <span className="text-[#2563EB]">Comparison</span>
            </h2>
            <p className="text-[#94A3B8] text-lg">Detailed breakdown of features and benefits for each tier</p>
          </div>

          <div className="space-y-6">
            {tiers.map((tier, index) => (
              <div
                key={tier.id}
                className={`bg-gradient-to-br from-[#111827] to-[#0A0F1C] border rounded-2xl p-6 transition-all ${
                  selectedTier === tier.id 
                    ? 'border-[#2563EB] shadow-lg shadow-[#2563EB]/20' 
                    : 'border-[#2563EB]/20'
                }`}
              >
                <div className="grid md:grid-cols-12 gap-6">
                  {/* Tier Info */}
                  <div className="md:col-span-3">
                    <div className="flex items-center gap-3 mb-2">
                      {tier.isFree && (
                        <span className="bg-green-500/20 text-green-400 text-xs px-2 py-1 rounded-full">Free Trial</span>
                      )}
                      {tier.popular && (
                        <span className="bg-[#2563EB]/20 text-[#2563EB] text-xs px-2 py-1 rounded-full">Most Popular</span>
                      )}
                    </div>
                    <h3 className="text-2xl font-bold text-[#F8FAFC]">{tier.name}</h3>
                    <p className="text-[#2563EB] font-medium mt-1">{tier.tagline}</p>
                    <div className="mt-3">
                      {typeof tier.price === 'number' ? (
                        <div className="flex items-baseline">
                          <span className="text-3xl font-bold text-[#F8FAFC]">{currentCurrency.symbol}{Math.round(tier.price * currentCurrency.rate)}</span>
                          <span className="text-[#94A3B8] ml-1">/mo</span>
                        </div>
                      ) : (
                        <span className="text-2xl font-bold text-[#F8FAFC]">{tier.price}</span>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="md:col-span-3">
                    <h4 className="text-sm font-semibold text-[#94A3B8] uppercase tracking-wide mb-2">Description</h4>
                    <p className="text-[#F8FAFC] text-sm leading-relaxed">{tier.description}</p>
                    <div className="mt-3 pt-3 border-t border-[#2563EB]/10">
                      <p className="text-xs text-[#94A3B8]">Best for:</p>
                      <p className="text-sm text-[#2563EB] font-medium">{tier.bestFor}</p>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="md:col-span-3">
                    <h4 className="text-sm font-semibold text-[#94A3B8] uppercase tracking-wide mb-2">Features</h4>
                    <ul className="space-y-2">
                      {tier.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm">
                          <Check className="w-4 h-4 text-[#2563EB] flex-shrink-0" />
                          <span className="text-[#F8FAFC]">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Benefits */}
                  <div className="md:col-span-3">
                    <h4 className="text-sm font-semibold text-[#94A3B8] uppercase tracking-wide mb-2">Key Benefits</h4>
                    <ul className="space-y-2">
                      {tier.benefits.map((benefit, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <Zap className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                          <span className="text-[#94A3B8]">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Select Button */}
                <div className="mt-6 pt-4 border-t border-[#2563EB]/10 flex justify-end">
                  <Button
                    onClick={() => setSelectedTier(tier.id)}
                    className={`px-6 py-2 ${
                      selectedTier === tier.id
                        ? 'bg-[#2563EB] text-white'
                        : 'bg-[#2563EB]/10 text-[#2563EB] hover:bg-[#2563EB]/20'
                    }`}
                  >
                    {selectedTier === tier.id ? (
                      <><Check className="w-4 h-4 mr-2" /> Selected</>
                    ) : (
                      'Select This Plan'
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Step 2: Feature Modules */}
      <section className="py-16 bg-gradient-to-b from-[#0A0F1C] to-[#111827] px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#F8FAFC] mb-3">
              Step 2: Add <span className="text-[#2563EB]">Feature Modules</span>
            </h2>
            <p className="text-[#94A3B8] text-lg">All features available at any tier. Buy individual features ($49 each) or save with module bundles.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {featureModules.map((module) => (
              <div
                key={module.id}
                className={`bg-gradient-to-br from-[#111827] to-[#0A0F1C] border rounded-2xl p-6 transition-all duration-300 ${
                  selectedBundles.includes(module.id)
                    ? 'border-[#2563EB] shadow-lg shadow-[#2563EB]/20'
                    : 'border-[#2563EB]/20 hover:border-[#2563EB]/40'
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-[#F8FAFC] mb-1">{module.name}</h3>
                    <p className="text-[#94A3B8] text-sm">{module.description}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-[#F8FAFC]">${module.bundlePrice}<span className="text-sm text-[#94A3B8]">/mo</span></div>
                    <div className="text-xs text-green-400 font-semibold">Save ${module.savings} ({module.savingsPercent}% off)</div>
                  </div>
                </div>

                <Button
                  onClick={() => toggleBundle(module.id)}
                  className={`w-full mb-4 ${
                    selectedBundles.includes(module.id)
                      ? 'bg-[#2563EB] hover:bg-[#1d4ed8] text-white'
                      : 'bg-[#2563EB]/10 hover:bg-[#2563EB]/20 text-[#2563EB] border border-[#2563EB]/30'
                  }`}
                >
                  {selectedBundles.includes(module.id) ? (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Bundle Added
                    </>
                  ) : (
                    <>
                      <Package className="w-4 h-4 mr-2" />
                      Buy Bundle
                    </>
                  )}
                </Button>

                <div className="grid grid-cols-2 gap-2">
                  {module.features.map((feature) => {
                    const isIncludedInBundle = selectedBundles.includes(module.id);
                    const isSelected = selectedFeatures.includes(feature.id) || isIncludedInBundle;
                    
                    return (
                      <div
                        key={feature.id}
                        onClick={() => toggleFeature(feature.id, module.id)}
                        className={`flex items-center gap-2 p-2 rounded-lg text-sm transition-all ${
                          isIncludedInBundle
                            ? 'bg-[#2563EB]/20 text-[#F8FAFC] cursor-default'
                            : isSelected
                            ? 'bg-[#2563EB]/20 text-[#F8FAFC] cursor-pointer'
                            : 'bg-[#0A0F1C]/50 text-[#94A3B8] hover:bg-[#2563EB]/10 cursor-pointer'
                        }`}
                      >
                        <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                          isSelected ? 'bg-[#2563EB] border-[#2563EB]' : 'border-[#94A3B8]/30'
                        }`}>
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <span className="truncate">{feature.name}</span>
                        {feature.included && (
                          <span className="text-xs text-green-400 ml-auto">(Incl)</span>
                        )}
                        {!isIncludedInBundle && !feature.included && (
                          <span className="text-xs text-[#94A3B8] ml-auto">{currentCurrency.symbol}{Math.round(feature.price * currentCurrency.rate)}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sticky Selection Summary */}
      <div className="fixed bottom-0 left-0 right-0 bg-[#111827]/95 backdrop-blur-lg border-t border-[#2563EB]/30 py-4 px-6 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-8">
            <div>
              <p className="text-[#94A3B8] text-sm">Current Tier</p>
              <p className="text-[#F8FAFC] font-bold text-lg capitalize">{tiers.find(t => t.id === selectedTier)?.name} Plan</p>
            </div>
            <div>
              <p className="text-[#94A3B8] text-sm">Features Selected</p>
              <p className="text-[#F8FAFC] font-bold text-lg">
                {selectedBundles.length} bundles, {selectedFeatures.length} individual
              </p>
            </div>
            {calculateSavings() > 0 && (
              <div>
                <p className="text-[#94A3B8] text-sm">You're Saving</p>
                <p className="text-green-400 font-bold text-lg">{format(calculateSavings())}</p>
              </div>
            )}
          </div>
          <div className="flex items-center gap-6">
            <div className="text-right">
              <p className="text-[#94A3B8] text-sm">Total Monthly Cost</p>
              <p className="text-[#F8FAFC] font-bold text-3xl">
                {selectedTier === 'enterprise' ? 'Custom' : selectedTier === 'freemium' && calculateTotal() === 0 ? 'Free' : format(calculateTotal())}
              </p>
            </div>
            <Button 
              onClick={handleCheckout}
              disabled={isLoading}
              className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-8 py-6 text-lg font-semibold rounded-lg disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Processing...
                </>
              ) : selectedTier === 'freemium' && !isAuthenticated ? (
                <>
                  <Gift className="w-5 h-5 mr-2" />
                  Start Free Trial
                </>
              ) : selectedTier === 'enterprise' ? (
                <>
                  Contact Sales
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              ) : !isAuthenticated ? (
                <>
                  <LogIn className="w-5 h-5 mr-2" />
                  Sign Up to Continue
                </>
              ) : (
                <>
                  <Check className="w-5 h-5 mr-2" />
                  Proceed to Checkout
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Trust Section */}
      <section className="py-16 bg-[#0A0F1C] px-6 mb-20">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-8 mb-8">
            <div className="flex items-center gap-2 text-[#94A3B8]">
              <Shield className="w-5 h-5 text-[#2563EB]" />
              <span>Secure payment via Stripe</span>
            </div>
            <div className="flex items-center gap-2 text-[#94A3B8]">
              <Zap className="w-5 h-5 text-[#2563EB]" />
              <span>Instant access after payment</span>
            </div>
            <div className="flex items-center gap-2 text-[#94A3B8]">
              <Lock className="w-5 h-5 text-[#2563EB]" />
              <span>Cancel anytime</span>
            </div>
          </div>
        </div>
      </section>

      {/* Link to ROI Calculator */}
      <section className="py-16 px-6 bg-gradient-to-b from-[#0A0F1C] to-[#111827] mb-20">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-[#F8FAFC] mb-4">
            Calculate Your <span className="text-[#2563EB]">Potential ROI</span>
          </h2>
          <p className="text-xl text-[#94A3B8] mb-8 max-w-2xl mx-auto">
            See exactly how much PriceStrike could improve your margins and profitability
          </p>
          <Link to="/roi-calculator">
            <Button className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-12 py-7 text-lg font-semibold rounded-lg shadow-xl hover:shadow-[#2563EB]/50 transition-all duration-300">
              Open ROI Calculator
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
      
      {/* Simulation Dashboard Modal */}
      {showSimulation && (
        <SimulationDashboard 
          industry="retail"
          revenue="10-50m"
          onClose={() => setShowSimulation(false)}
        />
      )}
    </div>
  );
};

export default PricingPage;
